import { Chip, CopyToClipboardButton, IconButton, Popover, styled, Typography } from '@chronosphereio/chrono-ui';
import { ChevronDown, ChevronRight, CodeTags } from 'mdi-material-ui';
import { memo, useMemo, useState } from 'react';
import { extractVariablesFromQueries, indexToAlpha, QueryDefinition } from '@/core';

/** Max width for the popover content */
const POPOVER_MAX_WIDTH = 450;

/** Max height for the queries list before it scrolls */
const QUERIES_MAX_HEIGHT = 250;

/** Number of lines to show before clamping */
const LINE_CLAMP_LINES = 4;

/**
 * Approximate character threshold for showing expand button
 * Based on ~50 chars per line at 450px width
 */
const CLAMP_CHAR_THRESHOLD = LINE_CLAMP_LINES * 50;

const NON_BREAKING_SPACE = '\u00A0';

/** Extract query text from a query definition */
function getQueryText(q: QueryDefinition): string {
  const query = q.spec.plugin.spec?.query;
  return typeof query === 'string' ? query : '';
}

export type PanelQueryInfoProps = {
  queries?: QueryDefinition[];
  /** Fired after a query is successfully copied from the popover. */
  onCopyPanelQuery?: (info: { queryIndex: number; queryId?: string }) => void;
};

/**
 * Displays a code icon that shows a popover on hover with information about
 * the panel's queries and variables used.
 */
export const PanelQueryInfo = memo(function PanelQueryInfo({ queries, onCopyPanelQuery }: PanelQueryInfoProps) {
  if (!queries) return null;
  return (
    <Popover
      trigger="hover"
      Content={() => <PanelQueryInfoContent queries={queries} onCopyPanelQuery={onCopyPanelQuery} />}
    >
      <IconButton size="small" aria-label="Panel query info">
        <CodeTags />
      </IconButton>
    </Popover>
  );
});

type PanelQueryInfoContentProps = {
  queries: QueryDefinition[];
  onCopyPanelQuery?: (info: { queryIndex: number; queryId?: string }) => void;
};

/**
 * Only renders inner content when popover is open.
 */
function PanelQueryInfoContent({ queries, onCopyPanelQuery }: PanelQueryInfoContentProps) {
  const [expandedQueries, setExpandedQueries] = useState<Set<number>>(new Set());

  // Extract unique variables from all queries (memoized since queries rarely change)
  const variables = useMemo(() => extractVariablesFromQueries(queries), [queries]);

  const toggleExpanded = (idx: number) => {
    setExpandedQueries((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  return (
    <PopoverContent>
      <Typography variant="h4">{queries.length === 1 ? 'View query' : `View queries (${queries.length})`}</Typography>
      {variables.length > 0 && (
        <ChipsContainer>
          {variables.map((v) => (
            <Chip key={v} label={`$${v}`} />
          ))}
        </ChipsContainer>
      )}
      <QueriesContainer>
        {queries.map((q, idx) => {
          const queryText = getQueryText(q);
          const isExpanded = expandedQueries.has(idx);
          const needsClamp = queryText.length > CLAMP_CHAR_THRESHOLD;

          return (
            <QueryRow key={q.spec.id ? `id-${q.spec.id}` : `idx-${idx}`}>
              <QueryTextContainer>
                <QueryText variant="code" component="div" clamped={needsClamp && !isExpanded}>
                  <QueryLabel>{indexToAlpha(idx)}:</QueryLabel>
                  {NON_BREAKING_SPACE}
                  {queryText || <EmptyQuery>(empty query)</EmptyQuery>}
                </QueryText>
                {needsClamp && (
                  <ExpandButton
                    size="small"
                    onClick={() => toggleExpanded(idx)}
                    aria-label={isExpanded ? 'Collapse query' : 'Expand query'}
                  >
                    {isExpanded ? <ChevronDown fontSize="small" /> : <ChevronRight fontSize="small" />}
                  </ExpandButton>
                )}
              </QueryTextContainer>
              <CopyButton
                className="copy-button"
                size="small"
                valueToCopy={queryText}
                aria-label="Copy query"
                innerSx={{ fontSize: '1rem' }}
                onCopySuccess={() =>
                  onCopyPanelQuery?.({
                    queryIndex: idx,
                    queryId: q.spec.id,
                  })
                }
              />
            </QueryRow>
          );
        })}
      </QueriesContainer>
    </PopoverContent>
  );
}

const PopoverContent = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: POPOVER_MAX_WIDTH,
  };
});

const ChipsContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

const QueriesContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  maxHeight: QUERIES_MAX_HEIGHT,
  overflowY: 'auto',
});

const QueryRow = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  '&:hover .copy-button': {
    opacity: 1,
  },
});

const QueryTextContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '4px',
});

const QueryText = styled(Typography)<{ clamped?: boolean }>(({ clamped }) => ({
  flex: 1,
  wordBreak: 'break-word',
  ...(clamped && {
    display: '-webkit-box',
    WebkitLineClamp: LINE_CLAMP_LINES,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }),
}));

const QueryLabel = styled('span')({
  fontWeight: 600,
});

const EmptyQuery = styled('span')(({ theme }) => ({
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
}));

const ExpandButton = styled(IconButton)({
  flexShrink: 0,
  marginTop: '-4px',
});

const CopyButton = styled(CopyToClipboardButton)({
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0,
  transition: 'opacity 0.15s ease-in-out',
});
